// Record Inspection Modal
// Allows users to log completed inspections with date, outcome, and documentation

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Upload, X, FileText, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Inspection outcomes
const INSPECTION_OUTCOMES = [
  { value: 'Pass', label: 'Pass' },
  { value: 'Fail', label: 'Fail' },
  { value: 'PassWithAdvisory', label: 'Pass with Advisory' },
  { value: 'ConditionalPass', label: 'Conditional Pass' },
] as const;

// Form validation schema
const formSchema = z.object({
  inspectionDate: z.date({
    message: 'Inspection date is required',
  }).refine((date) => date <= new Date(), {
    message: 'Inspection date cannot be in the future',
  }),
  inspectorName: z.string().min(1, 'Inspector name is required'),
  outcome: z.enum(['Pass', 'Fail', 'PassWithAdvisory', 'ConditionalPass'], {
    message: 'Outcome is required',
  }),
  notes: z.string().optional(),
  cost: z.string().optional(),
  serviceProvider: z.string().optional(),
  nextInspectionDate: z.date().optional(),
}).refine((data) => {
  // Notes required if outcome is not "Pass"
  if (data.outcome !== 'Pass' && (!data.notes || data.notes.trim().length === 0)) {
    return false;
  }
  return true;
}, {
  message: 'Notes are required when outcome is not "Pass"',
  path: ['notes'],
});

type FormValues = z.infer<typeof formSchema>;

interface RecordInspectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  complianceItem?: {
    id: number;
    assetName: string;
    regimeName: string;
    frequency?: string;
  };
  onSubmit: (data: FormValues & { files: File[] }) => Promise<void>;
}

export function RecordInspectionModal({
  open,
  onOpenChange,
  complianceItem,
  onSubmit,
}: Readonly<RecordInspectionModalProps>) {
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      inspectionDate: new Date(),
      inspectorName: '',
      outcome: undefined,
      notes: '',
      cost: '',
      serviceProvider: '',
      nextInspectionDate: undefined,
    },
  });

  // Calculate next inspection date based on frequency
  const calculateNextInspectionDate = (frequency?: string): Date | undefined => {
    if (!frequency) return undefined;
    
    const today = new Date();
    const frequencyMap: Record<string, number> = {
      'Daily': 1,
      'Weekly': 7,
      'Monthly': 30,
      'Quarterly': 90,
      'Semi-Annually': 180,
      'Annually': 365,
      'Biennial': 730,
    };

    const days = frequencyMap[frequency];
    if (!days) return undefined;

    const nextDate = new Date(today);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
  };

  // Auto-calculate next inspection date when outcome changes
  const handleOutcomeChange = (outcome: string) => {
    if (outcome === 'Pass' && complianceItem?.frequency) {
      const nextDate = calculateNextInspectionDate(complianceItem.frequency);
      if (nextDate) {
        form.setValue('nextInspectionDate', nextDate);
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (data: FormValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit({ ...data, files });
      toast.success('Inspection recorded successfully');
      form.reset();
      setFiles([]);
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to record inspection');
      console.error('Error recording inspection:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record Inspection</DialogTitle>
          <DialogDescription>
            {complianceItem ? (
              <>
                Log a completed inspection for <strong>{complianceItem.assetName}</strong> - {complianceItem.regimeName}
              </>
            ) : (
              'Log a completed inspection with date, outcome, and documentation'
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Inspection Date */}
            <FormField
              control={form.control}
              name="inspectionDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Inspection Date *</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                        >
                          {field.value ? (
                            format(field.value, 'PPP')
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) => date > new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    The date the inspection was completed (cannot be in the future)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Inspector Name */}
            <FormField
              control={form.control}
              name="inspectorName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Inspector Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter inspector name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Outcome */}
            <FormField
              control={form.control}
              name="outcome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Outcome *</FormLabel>
                  <Select
                    onValueChange={(value: string) => {
                      field.onChange(value);
                      handleOutcomeChange(value);
                    }}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select inspection outcome" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {INSPECTION_OUTCOMES.map((outcome) => (
                        <SelectItem key={outcome.value} value={outcome.value}>
                          {outcome.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Notes/Findings */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Notes/Findings {form.watch('outcome') !== 'Pass' && '*'}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter inspection findings and notes"
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {form.watch('outcome') !== 'Pass' && 'Required when outcome is not "Pass"'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Cost (Optional) */}
            <FormField
              control={form.control}
              name="cost"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cost (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Service Provider (Optional) */}
            <FormField
              control={form.control}
              name="serviceProvider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service Provider (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter service provider name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Next Inspection Date */}
            <FormField
              control={form.control}
              name="nextInspectionDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Next Inspection Date (Optional)</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                        >
                          {field.value ? (
                            format(field.value, 'PPP')
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    Auto-calculated based on compliance frequency, can be overridden
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* File Upload */}
            <div className="space-y-2">
              <FormLabel>Documentation (Recommended)</FormLabel>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('file-upload')?.click()}
                  className="w-full"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Files
                </Button>
              </div>
              {files.length > 0 && (
                <div className="space-y-2 mt-2">
                  {files.map((file) => (
                    <div
                      key={`${file.name}-${file.size}`}
                      className="flex items-center justify-between p-2 bg-muted rounded-md"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(files.indexOf(file))}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Record Inspection
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
